import React, { useState } from 'react';
import { Keyboard, View } from 'react-native';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { Input, InputField } from '@/components/gluestack/input';
import { FormControl, FormControlError, FormControlErrorIcon, FormControlErrorText, FormControlHelper, FormControlHelperText, FormControlLabel, FormControlLabelText } from '@/components/gluestack/form-control';
import { Button, ButtonText } from '@/components/gluestack/button';
import { AlertCircleIcon } from '@/components/gluestack/icon';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useToastMessages } from '@/hooks/useToastMessages';
import { AccountService } from '@/services/domain/AccountService';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';

type FormValues = { email: string };

export interface IEmailChangeSheetProps extends IContextSheetContentProps {}

export default function EmailChangeSheet({ onClose }: IEmailChangeSheetProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useDelayedAutoFocus();

  const schema = z.object({
    email: z.string().email({ message: t('settings.account.email.invalid') }),
  });

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = async ({ email }: FormValues) => {
    Keyboard.dismiss();
    setSubmitting(true);
    try {
      try {
        await AccountService.isEmailAvailable(email);
      } catch (e) {
        setError('email', { type: 'validate', message: t('settings.account.email.unavailable') });
        setSubmitting(false);
        return;
      }

      await AccountService.changeEmail(email);
      showSuccessToast(t('settings.account.email.success'));
      onClose?.();
    } catch (error) {
      showErrorToast(t('settings.account.email.error'), error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VStack className="flex-1 gap-4">
      <FormControl isInvalid={!!errors.email}>
        <FormControlLabel>
          <FormControlLabelText>{t('settings.account.email.label')}</FormControlLabelText>
        </FormControlLabel>
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                ref={inputRef}
                placeholder={t('settings.account.email.placeholder')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                returnKeyType="done"
                onSubmitEditing={handleSubmit(onSubmit)}
              />
            </Input>
          )}
        />
        <FormControlHelper>
          <FormControlHelperText>{t('settings.account.email.changeExplanation')}</FormControlHelperText>
        </FormControlHelper>
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText>{errors.email?.message}</FormControlErrorText>
        </FormControlError>
      </FormControl>

      <View className="flex-1" />

      <Button onPress={handleSubmit(onSubmit)} isDisabled={submitting} className="mt-2 self-end">
        <ButtonText>{t('common.update')}</ButtonText>
      </Button>
    </VStack>
  );
}

EmailChangeSheet.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'sm',
  expectsKeyboard: true,
} satisfies ISheetConfig;
